import { useNavigate, useParams } from 'react-router-dom'

import { TrainingClassFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'

import type { ParticipantInfo } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/ImportCertificates/types'
import type { TrainingsTrainingMembership } from './useDataSource'

export interface BulkSelectionFilters {
  search?: string
  status?: string
  teamId?: number
  classId?: number
  dueDate?: string
}

export type BulkSelection =
  | { type: 'manual'; ids: number[] }
  | { type: 'all'; excludedIds: number[]; filters?: BulkSelectionFilters }

interface SelectedItem {
  item: TrainingsTrainingMembership
  checked: boolean
}

interface BulkActionParams {
  itemsStatus: readonly SelectedItem[]
  allSelected?: boolean | 'indeterminate'
  selectedCount?: number
}

const buildSelection = (
  params: BulkActionParams,
  getItemId: (item: TrainingsTrainingMembership) => number | undefined,
  filters?: BulkSelectionFilters,
  getExcludedItemId?: (item: TrainingsTrainingMembership) => number | undefined
): BulkSelection => {
  const isSelectAll = params.allSelected === true || params.allSelected === 'indeterminate'

  const ids = params.itemsStatus
    .filter((status) => status.checked)
    .map((status) => getItemId(status.item))
    .filter((id): id is number => id !== undefined)

  if (isSelectAll) {
    const excludedIdExtractor = getExcludedItemId ?? getItemId
    const excludedIds =
      params.allSelected === 'indeterminate'
        ? params.itemsStatus
            .filter((status) => !status.checked)
            .map((status) => excludedIdExtractor(status.item))
            .filter((id): id is number => id !== undefined)
        : []

    return { type: 'all', excludedIds, filters }
  }

  return { type: 'manual', ids }
}

export interface ExportCertificatesHandlers {
  onBulkExport: (selection: BulkSelection, selectedEmployeeIds: number[]) => void
}

export interface ImportCertificatesHandlers {
  onBulkImport: (selection: BulkSelection, participants: ParticipantInfo[]) => void
}

export const useBulkAction = (
  trainingClasses: TrainingClassFragment[],
  exportCertificatesHandlers: ExportCertificatesHandlers,
  importCertificatesHandlers: ImportCertificatesHandlers | undefined,
  currentFilters?: BulkSelectionFilters
) => {
  const { id } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  return (action: string, selectedRows: BulkActionParams) => {
    switch (action) {
      case 'delete-all': {
        const selection = buildSelection(selectedRows, (item) => item?.id, currentFilters)

        navigate(
          navigation.trainings.list.details_revamp.view.participants.deleteParticipants.fullPath({
            id: Number(id),
          }),
          { state: { selection } }
        )
        break
      }

      case 'assign-to-group': {
        const selection = buildSelection(
          selectedRows,
          (item) => item?.access?.id,
          currentFilters,
          (item) => item?.employee?.id
        )
        const hasNoClasses = trainingClasses.length === 0

        const path = hasNoClasses
          ? navigation.trainings.list.details_revamp.view.trainingClasses.tab.newClass.fullPath({
              id: Number(id),
            })
          : navigation.trainings.list.details_revamp.view.participants.assignParticipants.fullPath({
              id: Number(id),
            })

        navigate(path, { state: { selection } })
        break
      }

      case 'export-certificates': {
        const selection = buildSelection(selectedRows, (item) => item?.id, currentFilters)

        const selectedEmployeeIds = selectedRows.itemsStatus
          .filter((status) => status.checked)
          .map((status) => status.item?.employee?.id)
          .filter((employeeId): employeeId is number => employeeId !== undefined)

        exportCertificatesHandlers.onBulkExport(selection, selectedEmployeeIds)
        break
      }

      case 'import-certificates': {
        if (!importCertificatesHandlers) break

        const selection = buildSelection(selectedRows, (item) => item?.id, currentFilters)

        const participants: ParticipantInfo[] = selectedRows.itemsStatus
          .filter(
            (
              status
            ): status is typeof status & {
              item: { employee: NonNullable<typeof status.item.employee> }
            } =>
              status.checked &&
              status.item?.employee !== undefined &&
              status.item?.employee !== null
          )
          .map((status) => ({
            membershipId: status.item.id,
            employeeId: status.item.employee.id,
            employeeName: status.item.employee.fullName ?? '',
          }))

        importCertificatesHandlers.onBulkImport(selection, participants)
        break
      }
    }
  }
}
